export type MassUnit = "oz" | "g" | "lb" | "kg";

// Object pool for referential equality
const massPool = new Map<number, Mass>();

const factors = {
  oz: 28.3495,
  lbs: 28.3495 * 16,
  g: 1,
  kg: 1000,
} as const;

export class Mass {
  private constructor(private grams: number) {}

  private static addToMap(grams: number): Mass {
    const value = new Mass(grams);
    massPool.set(grams, value);
    return value;
  }

  static from(value: number, unit: MassUnit) {
    const grams = convertToGrams(value, unit);
    return massPool.get(grams) ?? Mass.addToMap(grams);
  }

  mul(rhs: number) {
    return Mass.from(this.asGrams() * rhs, "g");
  }

  asPounds() {
    return this.grams / factors.lbs;
  }

  asKilograms() {
    return this.grams / factors.kg;
  }

  asOunces() {
    return this.grams / factors.oz;
  }

  asGrams() {
    return this.grams;
  }
}

function convertToGrams(value: number, unit: MassUnit) {
  switch (unit) {
    case "lb":
      return value * factors.lbs;
    case "oz":
      return value * factors.oz;
    case "kg":
      return value * factors.kg;
    default:
      return value;
  }
}

export function validateUnit(unit: string): MassUnit {
  const normalizedUnit = unit.trim().toLowerCase();
  switch (normalizedUnit) {
    case "ounces":
    case "oz":
      return "oz";
    case "pounds":
    case "lb":
      return "lb";
    case "kilograms":
    case "kg":
      return "kg";
    case "grams":
    case "g":
      return "g";
    default:
      throw new Error(`unrecognized unit of mass: ${normalizedUnit}`);
  }
}
